import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Layout from "../Layout";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { Upload } from "lucide-react";
import {
  DynamicModuleService,
  SchemaService,
  UploadService,
} from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import { notify } from "@/components/common/Notification";
import { toast } from "sonner";
import navigationRoutes from "@/project/propertyPortal/utils/navigationRoutes";

// Fields that are allowed to be edited
const EDITABLE_FIELDS = ["propertyname", "sqfootage"];

// Check if a property is a layout component (Card, FormTab, FormLayout, etc.)
const isLayoutComponent = (property: any): boolean => {
  if (!property || typeof property !== "object") {
    return false;
  }

  // Layout components are typically void type with x-component
  const isVoid = property.type === "void";
  const hasXComponent =
    property["x-component"] && typeof property["x-component"] === "string";

  // Common layout components
  const layoutComponents = [
    "Card",
    "FormTab",
    "FormLayout",
    "FormGrid",
    "Tabs",
    "FormItem",
  ];
  const componentName = property["x-component"];

  return isVoid && hasXComponent && layoutComponents.includes(componentName);
};

// Clean up component props to remove non-DOM attributes
const cleanComponentProps = (props: any): any => {
  if (!props || typeof props !== "object") {
    return props;
  }

  // Remove is-in-grid and other non-DOM boolean attributes
  // These are Formily internal props that shouldn't be passed to DOM elements
  const cleaned: any = { ...props };
  delete cleaned["is-in-grid"];

  return cleaned;
};

// Recursively modify schema to only show editable fields (hide all others)
const modifySchemaToRestrictFields = (schema: any): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  // Handle schema with nested schema property
  if (schema.schema) {
    return {
      ...schema,
      schema: modifySchemaToRestrictFields(schema.schema),
    };
  }

  // If it's a property definition
  if (schema.properties) {
    const modifiedProperties: any = {};
    Object.keys(schema.properties).forEach(key => {
      const property = schema.properties[key];

      // Check if this is a layout component (Card, FormTab, etc.)
      if (isLayoutComponent(property)) {
        // Keep layout components and recursively process their properties
        const modifiedLayout = modifySchemaToRestrictFields(property);
        // Clean up component props for layout components
        if (modifiedLayout["x-component-props"]) {
          modifiedLayout["x-component-props"] = cleanComponentProps(
            modifiedLayout["x-component-props"]
          );
        }
        // Only include layout component if it has properties after filtering
        if (
          modifiedLayout?.properties &&
          Object.keys(modifiedLayout.properties).length > 0
        ) {
          modifiedProperties[key] = modifiedLayout;
        }
      } else {
        // For actual input fields, check if they're editable
        const isEditable = EDITABLE_FIELDS.some(
          field => field.toLowerCase() === key.toLowerCase()
        );

        // Only include fields that are in the editable list
        if (isEditable) {
          const modifiedProperty = modifySchemaToRestrictFields(property);
          // Clean up component props for this property
          if (modifiedProperty["x-component-props"]) {
            modifiedProperty["x-component-props"] = cleanComponentProps(
              modifiedProperty["x-component-props"]
            );
          }
          modifiedProperties[key] = modifiedProperty;
        }
        // Completely exclude non-editable fields
      }
    });

    // Return modified schema with filtered properties
    const result: any = {
      ...schema,
      properties: modifiedProperties,
    };

    // Clean up component props at the schema level too
    if (result["x-component-props"]) {
      result["x-component-props"] = cleanComponentProps(
        result["x-component-props"]
      );
    }

    return result;
  }

  // If it's an array of items
  if (schema.items) {
    return {
      ...schema,
      items: modifySchemaToRestrictFields(schema.items),
    };
  }

  // Recursively process other object properties
  const modified: any = { ...schema };
  Object.keys(schema).forEach(key => {
    if (
      key !== "properties" &&
      key !== "items" &&
      key !== "schema" &&
      typeof schema[key] === "object"
    ) {
      modified[key] = modifySchemaToRestrictFields(schema[key]);
    }
  });

  // Clean up x-component-props to remove non-DOM attributes
  if (modified["x-component-props"]) {
    modified["x-component-props"] = cleanComponentProps(
      modified["x-component-props"]
    );
  }

  return modified;
};

const PP_EditRehabEstimator: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const schemaName = "Property Master";
  const rehabEstimateSchemaName = "Rehab Estimate";
  const myPropertySchemaName = "My Property";
  const maxBidSchemaName = "Max Bid";

  const form = useMemo(() => createForm({}), []);

  // Rehab type to value mapping
  const rehabTypeValues: Record<string, string> = useMemo(
    () => ({
      EXCELLENT: "$20",
      GOOD: "$30",
      OK: "$40",
      ROUGH: "$50",
      "VACANT ROUGH": "$65",
      BAD: "$80",
    }),
    []
  );

  // Rehab type and value state
  const [rehabType, setRehabType] = useState<string>("");
  const [rehabValue, setRehabValue] = useState<string>("");
  const [fileuploads, setfileuploads] = useState<{
    fileupload: any[];
  }>({
    fileupload: [],
  });

  // Refs for file inputs
  const fileInputRefs = useRef<{
    fileupload: HTMLInputElement | null;
  }>({
    fileupload: null,
  });
  const [remarks, setRemarks] = useState<string>("");
  const [additionalNotes, setAdditionalNotes] = useState<string>("");
  const [myPropertyRecordId, setMyPropertyRecordId] = useState<string | null>(
    null
  );
  // State to store file info (ID -> file info mapping) for displaying file names/URLs
  const [fileInfoMap, setFileInfoMap] = useState<Record<string, any>>({});

  // Rehab estimate data state
  const [rehabEstimateData, setRehabEstimateData] = useState<{
    rehabestimate: string | null;
  }>({
    rehabestimate: null,
  });

  const [maxBidValue, setMaxBidValue] = useState<string | null>(null);

  // Store the rehab estimate record ID for updates
  const [rehabEstimateRecordId, setRehabEstimateRecordId] = useState<
    string | null
  >(null);

  // Flag to track if initial data has been loaded (to prevent overwriting during initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update rehab value when rehab type changes
  useEffect(() => {
    if (rehabType && rehabTypeValues[rehabType]) {
      setRehabValue(rehabTypeValues[rehabType]);
    } else {
      setRehabValue("");
    }
  }, [rehabType, rehabTypeValues]);

  // Get schema columns first to know what fields to fetch
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", rehabEstimateSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          rehabEstimateSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!rehabEstimateSchemaName,
  });

  const { data: maxBidColumnData } = useQuery({
    queryKey: ["columnGridData", maxBidSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(maxBidSchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!maxBidSchemaName,
  });

  // Get My Property schema columns first
  const { data: myPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", myPropertySchemaName],
    queryFn: async () => {
      try {
        const result =
          await SchemaService.getColumnGridData(myPropertySchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!myPropertySchemaName,
  });

  // Fetch My Property data filtered by propertymasterid
  const { data: myPropertyResponse, refetch: refetchMyProperty } = useQuery({
    queryKey: ["my-property", myPropertySchemaName, id, myPropertyColumnData],
    queryFn: async () => {
      if (!myPropertyColumnData?.grid || !id) {
        return { results: [], count: 0 };
      }

      // Get all column names from schema (ONLY use fields that actually exist in schema)
      const schemaColumns = myPropertyColumnData.grid.map(
        (field: any) => field.field_name
      );

      console.log("My Property schema field names:", schemaColumns);

      // Build columns list - only use schema fields + id (if needed for API)
      const allColumns: string[] = [...schemaColumns];
      const lowerColumns = schemaColumns.map((col: string) =>
        col.toLowerCase()
      );

      // Only add 'id' if it doesn't exist and might be needed by API
      // But be careful - if API rejects it, we'll catch the error
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      // Check if propertymasterid exists in schema (check variations)
      const propertyMasterIdVariations = [
        "propertymasterid",
        "propertymasterid",
        "property_master_id",
        "PropertyMasterId",
        "propertymasterid",
      ];
      const actualPropertyMasterIdField = schemaColumns.find((col: string) =>
        propertyMasterIdVariations.some(
          variation => col.toLowerCase() === variation.toLowerCase()
        )
      );

      const hasPropertyMasterId = !!actualPropertyMasterIdField;

      console.log(
        "PropertyMaster ID field found:",
        actualPropertyMasterIdField
      );
      console.log("Has propertymasterid:", hasPropertyMasterId);

      try {
        // Try with filter first, but also try without filter to see if any records exist
        let response;
        const filter: Record<string, string> = {};

        // Only use filter if propertymasterid field exists in schema
        // Use the actual field name from schema, not hardcoded "propertymasterid"
        if (actualPropertyMasterIdField) {
          filter[actualPropertyMasterIdField] = id;
        }

        console.log("Fetching My Property data with filter:", filter);
        console.log("Filter field name:", actualPropertyMasterIdField);
        console.log("All columns:", allColumns);
        console.log("Has propertymasterid in schema:", hasPropertyMasterId);

        // First try with filter
        // Only use columns that exist in schema to avoid 400 errors
        const schemaLowerColumns = schemaColumns.map((col: string) =>
          col.toLowerCase()
        );
        const validColumns = allColumns.filter((col: string) => {
          const lowerCol = col.toLowerCase();
          // Include id if needed (API might require it)
          if (lowerCol === "id") {
            return true;
          }
          // Only include columns that exist in schema
          return schemaLowerColumns.includes(lowerCol);
        });

        if (Object.keys(filter).length > 0) {
          try {
            response = await DynamicModuleService.getGridCoByConfig(
              myPropertySchemaName,
              1,
              1000,
              validColumns,
              filter
            );
            console.log("Response with filter:", response);
          } catch (filterError: any) {
            console.error("Error fetching with filter:", filterError);
            // If filter fails, try without filter
            response = undefined;
          }
        }

        // If no results with filter, try without filter to see if any records exist at all
        if (
          !response ||
          (Array.isArray(response) && response.length === 0) ||
          ((response as any)?.results &&
            (response as any).results.length === 0) ||
          ((response as any)?.data && (response as any).data.length === 0)
        ) {
          console.log(
            "No results with filter, trying without filter to check if any records exist"
          );
          // Use only valid columns (schema fields + id if needed)
          const schemaLowerColumnsForAll = schemaColumns.map((col: string) =>
            col.toLowerCase()
          );
          const validColumnsForAll = allColumns.filter((col: string) => {
            const lowerCol = col.toLowerCase();
            // Include id if needed (API might require it)
            if (lowerCol === "id") {
              return true;
            }
            // Only include columns that exist in schema
            return schemaLowerColumnsForAll.includes(lowerCol);
          });

          const allResponse = await DynamicModuleService.getGridCoByConfig(
            myPropertySchemaName,
            1,
            10000,
            validColumnsForAll,
            undefined
          );
          console.log("Response without filter (all records):", allResponse);
          console.log(
            "Total records in My Property schema:",
            Array.isArray(allResponse)
              ? allResponse.length
              : ((allResponse as any)?.results?.length ??
                  (allResponse as any)?.data?.length ??
                  0)
          );

          // If we got results without filter, use those and filter client-side
          if (
            allResponse &&
            ((Array.isArray(allResponse) && allResponse.length > 0) ||
              ((allResponse as any)?.results &&
                (allResponse as any).results.length > 0) ||
              ((allResponse as any)?.data &&
                (allResponse as any).data.length > 0))
          ) {
            response = allResponse;
          }
        }

        console.log("My Property API response:", response);
        console.log("Response type:", typeof response);
        console.log("Is array:", Array.isArray(response));

        // Handle different response structures
        let results: any[] = [];
        const responseAny = response as any;

        if (Array.isArray(response)) {
          results = response;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        } else if (responseAny && typeof responseAny === "object") {
          // Try to find any array property
          const arrayKeys = Object.keys(responseAny).filter(key =>
            Array.isArray(responseAny[key])
          );
          if (arrayKeys.length > 0) {
            results = responseAny[arrayKeys[0]];
          }
        }

        console.log("Results before filtering:", results);
        console.log("Results count:", results.length);

        // Log the first record to see what fields it has
        if (Array.isArray(results) && results.length > 0) {
          console.log("First record keys:", Object.keys(results[0]));
          console.log("First record:", results[0]);
          console.log("Looking for propertymasterid matching:", id);

          // Check all possible propertymasterid field variations in the first record
          const firstRecord = results[0];
          const possibleFields = Object.keys(firstRecord).filter(
            key =>
              key.toLowerCase().includes("property") &&
              key.toLowerCase().includes("master")
          );
          console.log(
            "Possible propertymasterid fields in record:",
            possibleFields
          );

          // Check each record for propertymasterid
          results.forEach((record, index) => {
            const recordKeys = Object.keys(record);
            const propertyMasterFields = recordKeys.filter(
              key =>
                key.toLowerCase().includes("property") &&
                key.toLowerCase().includes("master")
            );
            if (propertyMasterFields.length > 0) {
              propertyMasterFields.forEach(field => {
                console.log(
                  `Record ${index} - ${field}:`,
                  record[field],
                  "matches?",
                  String(record[field]) === String(id)
                );
              });
            } else {
              console.log(
                `Record ${index} - No propertymasterid field found. All keys:`,
                recordKeys
              );
            }
          });
        }

        // Always filter by propertymasterid if we have results (even if server-side filter was used)
        if (Array.isArray(results) && results.length > 0) {
          // First, check if there are any records with null propertymasterid that might belong to this property
          // These need to be updated
          const recordsWithNullPropertyMasterId = results.filter(
            (record: any) => {
              const recordId =
                record.propertymasterid ??
                record.propertymasterid ??
                record.PropertyMasterId;
              return !recordId || recordId === null;
            }
          );

          if (recordsWithNullPropertyMasterId.length > 0) {
            console.log(
              `Found ${recordsWithNullPropertyMasterId.length} records with null propertymasterid. These may need to be updated.`
            );
          }

          // Filter for records that match the propertymasterid
          const filteredResults = results.filter((record: any) => {
            // Try the actual field name first, then multiple case variations
            let recordId = null;

            if (actualPropertyMasterIdField) {
              recordId = record[actualPropertyMasterIdField];
            }

            // Try multiple case variations if recordId is still null/undefined
            recordId ??=
              record.propertymasterid ??
              record.propertymasterid ??
              record.PropertyMasterId ??
              record.propertymasterid ??
              record.propertymasterid ??
              record["propertymasterid"] ??
              record["PropertyMasterId"] ??
              record["propertymasterid"] ??
              record["property_master_id"] ??
              null;

            // Also try to find any field that contains "property" and "master"
            if (!recordId) {
              const allKeys = Object.keys(record);
              const propertyMasterKey = allKeys.find(
                key =>
                  key.toLowerCase().includes("property") &&
                  key.toLowerCase().includes("master")
              );
              if (propertyMasterKey) {
                recordId = record[propertyMasterKey];
                console.log(
                  "Found propertymasterid using key:",
                  propertyMasterKey,
                  "value:",
                  recordId
                );
              }
            }

            const matches = recordId && String(recordId) === String(id);
            if (matches) {
              console.log(
                "Found matching record for propertymasterid",
                id,
                ":",
                record
              );
              console.log(
                "Record ID field used:",
                actualPropertyMasterIdField ?? "propertymasterid (variation)"
              );
            }
            return matches;
          });

          console.log(
            "Filtered results count (matching propertymasterid):",
            filteredResults.length
          );

          // If no matching records found, but we have records with null propertymasterid,
          // include the first one so it can be loaded and updated
          if (
            filteredResults.length === 0 &&
            recordsWithNullPropertyMasterId.length > 0
          ) {
            console.log(
              "No matching records found, but found records with null propertymasterid. Loading first one to update it."
            );
            // Get the first record with null propertymasterid
            results = [recordsWithNullPropertyMasterId[0]];
          } else {
            results = filteredResults;
          }
        } else if (Array.isArray(results)) {
          console.log("No results to filter");
        }

        console.log("My Property results after processing:", results);
        console.log("Final results count:", results.length);

        return { results, count: results.length };
      } catch (error) {
        console.error("Error fetching my property:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!myPropertySchemaName && !!myPropertyColumnData && !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Fetch Rehab Estimate data filtered by propertymasterid
  const { data: rehabEstimateResponse, refetch: refetchRehabEstimate } =
    useQuery({
      queryKey: [
        "rehab-estimate",
        rehabEstimateSchemaName,
        id,
        rehabEstimateColumnData,
      ],
      queryFn: async () => {
        if (!rehabEstimateColumnData?.grid || !id) {
          return { results: [], count: 0 };
        }

        // Get all column names from schema
        const allColumns = rehabEstimateColumnData.grid.map(
          (field: any) => field.field_name
        );

        // Explicitly add "id" if not already present
        const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
        if (!lowerColumns.includes("id")) {
          allColumns.push("id");
        }

        // Find the actual propertymasterid field name from schema
        const propertyMasterIdVariations = [
          "propertymasterid",
          "property_master_id",
          "PropertyMasterId",
          "propertymasterid",
        ];
        const actualPropertyMasterIdField = allColumns.find((col: string) =>
          propertyMasterIdVariations.some(
            variation => col.toLowerCase() === variation.toLowerCase()
          )
        );

        try {
          const filter: Record<string, string> = {};
          // Only use filter if propertymasterid field exists in schema
          if (actualPropertyMasterIdField) {
            filter[actualPropertyMasterIdField] = id;
          }

          const response = await DynamicModuleService.getGridCoByConfig(
            rehabEstimateSchemaName,
            1,
            1000,
            allColumns,
            Object.keys(filter).length > 0 ? filter : undefined
          );

          console.log("Rehab Estimate response:", response);
          return response ?? { results: [], count: 0 };
        } catch (error) {
          console.error("Error fetching rehab estimate:", error);
          return { results: [], count: 0 };
        }
      },
      enabled: !!rehabEstimateSchemaName && !!rehabEstimateColumnData && !!id,
    });

  const { data: maxBidResponse } = useQuery({
    queryKey: ["max-bid", maxBidSchemaName, id, maxBidColumnData],
    queryFn: async () => {
      if (!maxBidColumnData?.grid || !id) {
        return { results: [], count: 0 };
      }

      const allColumns = maxBidColumnData.grid.map(
        (field: any) => field.field_name
      );
      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      // Find the actual propertymasterid field name from schema
      const propertyMasterIdVariations = [
        "propertymasterid",
        "property_master_id",
        "PropertyMasterId",
        "propertymasterid",
      ];
      const actualPropertyMasterIdField = allColumns.find((col: string) =>
        propertyMasterIdVariations.some(
          variation => col.toLowerCase() === variation.toLowerCase()
        )
      );

      try {
        const filter: Record<string, string> = {};
        // Only use filter if propertymasterid field exists in schema
        // Use the actual field name from schema, not hardcoded "propertymasterid"
        if (actualPropertyMasterIdField) {
          filter[actualPropertyMasterIdField] = id;
        }

        const response = await DynamicModuleService.getGridCoByConfig(
          maxBidSchemaName,
          1,
          1000,
          allColumns,
          Object.keys(filter).length > 0 ? filter : undefined
        );

        return response ?? { results: [], count: 0 };
      } catch (error) {
        console.error("Error fetching max bid:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!maxBidSchemaName && !!maxBidColumnData && !!id,
  });

  // Update rehab estimate data when loaded
  useEffect(() => {
    if (rehabEstimateResponse) {
      const data =
        rehabEstimateResponse.results ??
        rehabEstimateResponse.data ??
        rehabEstimateResponse;

      let rehabEstimateArray: any[] = [];
      if (Array.isArray(data)) {
        rehabEstimateArray = data;
      } else if (data && Array.isArray(data.results)) {
        rehabEstimateArray = data.results;
      }

      // Get the first rehab estimate record for this property
      if (rehabEstimateArray.length > 0) {
        const firstRecord = rehabEstimateArray[0];
        // Normalize keys to lowercase for consistency
        const normalized: any = {};
        Object.keys(firstRecord).forEach(key => {
          normalized[key.toLowerCase()] = firstRecord[key];
        });

        // Store the record ID for updates
        const recordId = normalized.id ?? normalized._id ?? null;
        setRehabEstimateRecordId(recordId);

        const rehabData = {
          rehabestimate: normalized.rehabestimate ?? null,
        };
        console.log("Rehab Estimate data:", rehabData);
        setRehabEstimateData(rehabData);

        // Restore rehab type if it exists
        if (normalized.rehabtype) {
          setRehabType(normalized.rehabtype);
        }

        setIsInitialLoad(false); // Mark initial load as complete
      } else {
        // Reset to null if no data found
        setRehabEstimateRecordId(null);
        setRehabEstimateData({
          rehabestimate: null,
        });
        setIsInitialLoad(false); // Mark initial load as complete even if no data
      }
    } else {
      setRehabEstimateRecordId(null);
      setRehabEstimateData({
        rehabestimate: null,
      });
    }
  }, [rehabEstimateResponse]);

  // Helper function to parse upload values and normalize to array of file ID strings
  const parseUploadValue = (value: any): string[] => {
    if (!value) {
      return [];
    }

    // Handle string values (could be JSON string or single URL/ID)
    if (typeof value === "string") {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          // Extract IDs from array (could be strings or objects)
          return parsed
            .map(item => {
              if (typeof item === "string") {
                return item;
              }
              if (item?.id) {
                return String(item.id);
              }
              if (item?.file_id) {
                return String(item.file_id);
              }
              return null;
            })
            .filter((id): id is string => id !== null && id !== "");
        }
        // If parsed to an object, extract ID
        if (parsed && typeof parsed === "object") {
          const id = parsed.id ?? parsed.file_id;
          return id ? [String(id)] : [];
        }
        // If parsed to a string or other primitive, wrap it
        return parsed ? [String(parsed)] : [];
      } catch {
        // Not JSON, treat as single URL/ID string
        return value.trim() ? [value.trim()] : [];
      }
    }

    // Handle arrays
    if (Array.isArray(value)) {
      // Extract IDs from array items and filter out null/undefined/empty values
      return value
        .map(item => {
          if (typeof item === "string") {
            return item;
          }
          if (item?.id) {
            return String(item.id);
          }
          if (item?.file_id) {
            return String(item.file_id);
          }
          return null;
        })
        .filter(
          (id): id is string =>
            id !== null && id !== "" && id !== "undefined" && id !== "null"
        );
    }

    // Handle single objects
    if (typeof value === "object" && value !== null) {
      const id = value.id ?? value.file_id;
      return id ? [String(id)] : [];
    }

    return [];
  };

  // Update My Property data when loaded
  useEffect(() => {
    console.log("My Property response changed:", myPropertyResponse);

    if (myPropertyResponse?.results) {
      let myPropertyArray: any[] = [];
      if (Array.isArray(myPropertyResponse.results)) {
        myPropertyArray = myPropertyResponse.results;
      } else if (Array.isArray(myPropertyResponse)) {
        myPropertyArray = myPropertyResponse;
      }

      console.log("My Property array:", myPropertyArray);

      // Get the first My Property record for this property
      if (myPropertyArray.length > 0) {
        const firstRecord = myPropertyArray[0];
        console.log("Loading My Property record:", firstRecord);

        // Normalize keys to lowercase for consistency
        const normalized: any = {};
        Object.keys(firstRecord).forEach(key => {
          normalized[key.toLowerCase()] = firstRecord[key];
        });

        console.log("Normalized My Property data:", normalized);
        console.log(
          "Available keys in normalized data:",
          Object.keys(normalized)
        );

        // Store the record ID for updates
        const recordId = normalized.id ?? normalized._id ?? null;
        setMyPropertyRecordId(recordId);
        console.log("Set My Property record ID:", recordId);

        // Check if propertymasterid is null and fix it if needed
        const recordPropertyMasterId =
          normalized.propertymasterid ??
          firstRecord.propertymasterid ??
          firstRecord.propertymasterid ??
          firstRecord.PropertyMasterId;

        if (
          (!recordPropertyMasterId || recordPropertyMasterId === null) &&
          id &&
          recordId
        ) {
          console.warn(
            "Loaded record has null propertymasterid, updating it..."
          );
          // Update the record to set propertymasterid (async operation)
          (async () => {
            try {
              const updatePayload: Record<string, any> = {};
              // Find the actual field name from schema
              if (myPropertyColumnData?.grid) {
                const propertyMasterField = myPropertyColumnData.grid.find(
                  (field: any) =>
                    field.field_name?.toLowerCase() === "propertymasterid"
                );
                const fieldName =
                  propertyMasterField?.field_name ?? "propertymasterid";
                updatePayload[fieldName] = id;
                await DynamicModuleService.updateItem(
                  myPropertySchemaName,
                  recordId,
                  updatePayload
                );
                console.log("Updated propertymasterid for loaded record");
                // Refetch to get updated data
                setTimeout(() => {
                  refetchMyProperty();
                }, 500);
              }
            } catch (updateError) {
              console.error(
                "Error updating propertymasterid for loaded record:",
                updateError
              );
            }
          })();
        }

        // Load file uploads - check both normalized and original case
        // Try all possible field name variations
        const getfileuploadValue = (fieldName: string) => {
          const lowerFieldName = fieldName.toLowerCase();
          // Check normalized first
          if (normalized[lowerFieldName] !== undefined) {
            return normalized[lowerFieldName];
          }
          // Check original record with various case variations
          return (
            firstRecord[fieldName] ??
            firstRecord[fieldName.toUpperCase()] ??
            firstRecord[fieldName.toLowerCase()] ??
            firstRecord[
              fieldName.charAt(0).toUpperCase() +
                fieldName.slice(1).toLowerCase()
            ] ??
            null
          );
        };

        const fileUploadsData: any = {
          fileupload: parseUploadValue(
            normalized.fileupload ?? firstRecord.fileupload ?? []
          ),
        };

        console.log("Loading file uploads:", fileUploadsData);
        console.log("Raw file upload values:", {
          fileupload: getfileuploadValue("fileupload"),
        });
        console.log("Parsed file uploads:", fileUploadsData);

        // Only update if we have actual data or if we're resetting
        setfileuploads(fileUploadsData);

        // Load remarks and notes - check both normalized and original case
        const remarksValue =
          normalized.remarks ??
          firstRecord.remarks ??
          firstRecord.Remarks ??
          normalized["remarks"] ??
          "";
        const notesValue =
          normalized.notes ??
          firstRecord.notes ??
          firstRecord.Notes ??
          normalized["notes"] ??
          "";
        console.log("Loading remarks:", remarksValue, "notes:", notesValue);
        console.log("Raw remarks/notes values:", {
          remarks: normalized.remarks ?? firstRecord.remarks,
          notes: normalized.notes ?? firstRecord.notes,
        });
        setRemarks(remarksValue);
        setAdditionalNotes(notesValue);

        // Load rehab value if it exists (convert number to string format)
        if (
          normalized.rehabvalue !== null &&
          normalized.rehabvalue !== undefined
        ) {
          const rehabValueStr = `$${normalized.rehabvalue}`;
          console.log("Loading rehab value:", rehabValueStr);
          setRehabValue(rehabValueStr);
        }
      } else {
        console.log("No My Property records found, resetting to defaults");
        // Only reset if we actually got a response but it's empty
        // Don't reset if the query hasn't run yet
        if (myPropertyResponse.count === 0) {
          setMyPropertyRecordId(null);
          setRemarks("");
          setAdditionalNotes("");
          setfileuploads({
            fileupload: [],
          });
        }
      }
    }
    // Don't reset state if myPropertyResponse is undefined/null - the query might still be loading
  }, [myPropertyResponse, id, myPropertyColumnData?.grid, refetchMyProperty]);

  useEffect(() => {
    if (maxBidResponse) {
      const data =
        maxBidResponse.results ?? maxBidResponse.data ?? maxBidResponse;

      let maxBidArray: any[] = [];
      if (Array.isArray(data)) {
        maxBidArray = data;
      } else if (data && Array.isArray(data.results)) {
        maxBidArray = data.results;
      }

      if (maxBidArray.length > 0) {
        const firstRecord = maxBidArray[0];
        const normalized: any = {};
        Object.keys(firstRecord).forEach(key => {
          normalized[key.toLowerCase()] = firstRecord[key];
        });
        setMaxBidValue(normalized.maxbid ?? null);
      } else {
        setMaxBidValue(null);
      }
    } else {
      setMaxBidValue(null);
    }
  }, [maxBidResponse]);

  // Function to save/update rehab estimate
  const saveRehabEstimate = useCallback(
    async (estimateValue: string | null) => {
      if (!id || !estimateValue) {
        return;
      }

      try {
        const saveData: any = {
          propertymasterid: id,
          rehabestimate: estimateValue,
          rehabtype: rehabType ?? null, // Save rehab type along with estimate
        };

        if (rehabEstimateRecordId) {
          // Update existing record
          await DynamicModuleService.updateItem(
            rehabEstimateSchemaName,
            rehabEstimateRecordId,
            saveData
          );
          console.log("Rehab Estimate updated successfully");
        } else {
          // Create new record
          const response = await DynamicModuleService.createItem(
            rehabEstimateSchemaName,
            saveData
          );
          console.log("Rehab Estimate created successfully:", response);

          // Store the new record ID
          if (response?.id ?? response?.Id ?? response?.ID) {
            setRehabEstimateRecordId(response.id ?? response.Id ?? response.ID);
          }
        }

        // Refetch to ensure we have the latest data
        await refetchRehabEstimate();
      } catch (error: any) {
        console.error("Error saving rehab estimate:", error);
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          "Failed to save rehab estimate";
        notify.error(errorMessage);
      }
    },
    [
      id,
      rehabEstimateRecordId,
      rehabEstimateSchemaName,
      refetchRehabEstimate,
      rehabType,
    ]
  );

  // Calculate Rehab Estimate automatically: sqfootage * rehabvalue (but don't save automatically)
  useEffect(() => {
    // Don't calculate during initial load to preserve loaded value
    if (isInitialLoad) {
      return;
    }

    const sqfootage = form.values.sqfootage;
    const rehabValueStr = rehabValue;

    // Extract numeric value from rehabValue (e.g., "$20" -> 20)
    const numericRehabValue = rehabValueStr
      ? parseFloat(rehabValueStr.replace(/[^0-9.]/g, ""))
      : 0;

    // Only calculate if both values are available
    // Don't reset to null if values are missing - preserve existing loaded value
    if (sqfootage && numericRehabValue > 0) {
      const calculatedEstimate =
        (parseFloat(String(sqfootage)) ?? 0) * numericRehabValue;
      const estimateValue = calculatedEstimate.toString();

      // Only update if the calculated value is different from current value
      // This prevents unnecessary updates and preserves loaded values
      setRehabEstimateData(prev => {
        if (prev.rehabestimate !== estimateValue) {
          // Only update the display value, don't save automatically
          return {
            rehabestimate: estimateValue,
          };
        }
        return prev; // Keep existing value if it's the same
      });
    }
    // Removed the else block that was resetting to null
    // This preserves the loaded value when inputs are not available
  }, [form.values.sqfootage, rehabValue, isInitialLoad]);

  const { data: schemaJson } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
    enabled: !!schemaName,
  });

  const { data: recordInfo } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName ?? "", id ?? ""),
    enabled: !!schemaName && !!id,
  });

  const normalizedRecordInfo = useMemo(() => {
    if (!recordInfo) {
      return {};
    }
    const normalized: Record<string, any> = {};
    Object.keys(recordInfo).forEach(key => {
      normalized[key.toLowerCase()] = recordInfo[key];
    });
    return normalized;
  }, [recordInfo]);

  const getFieldValue = useCallback(
    (key: string) => {
      const lowerKey = key.toLowerCase();
      if (lowerKey === "propertyname") {
        return form.values.propertyname ?? normalizedRecordInfo[lowerKey];
      }
      if (lowerKey === "sqfootage") {
        return form.values.sqfootage ?? normalizedRecordInfo[lowerKey];
      }
      if (lowerKey === "rehabestimate") {
        return (
          rehabEstimateData.rehabestimate ?? normalizedRecordInfo[lowerKey]
        );
      }
      if (lowerKey === "maxbid") {
        return maxBidValue ?? normalizedRecordInfo[lowerKey];
      }
      return normalizedRecordInfo[lowerKey];
    },
    [
      form.values.propertyname,
      form.values.sqfootage,
      normalizedRecordInfo,
      rehabEstimateData.rehabestimate,
      maxBidValue,
    ]
  );

  const saveMyProperty = async () => {
    if (!id) {
      notify.error("Property ID missing");
      return;
    }

    const formatFiles = (files: any[]) =>
      (files || [])
        .map(f => {
          if (typeof f === "string") {
            return f;
          }
          if (f?.id) {
            return String(f.id);
          }
          if (f?.file_id) {
            return String(f.file_id);
          }
          if (typeof f === "object" && f !== null && f.id) {
            return String(f.id);
          }
          return String(f);
        })
        .filter(v => v && v !== "undefined" && v !== "null");

    const formattedFiles = formatFiles(fileuploads.fileupload);
    const fileUploadValue =
      formattedFiles.length > 0 ? JSON.stringify(formattedFiles) : null;

    const payload = {
      propertymasterid: id,
      fileupload: fileUploadValue,
      remarks: remarks?.trim() ? remarks.trim() : null,
      notes: additionalNotes?.trim() ? additionalNotes.trim() : null,
    };

    try {
      if (myPropertyRecordId) {
        await DynamicModuleService.updateItem(
          "My Property",
          myPropertyRecordId,
          payload
        );
      } else {
        const resp = await DynamicModuleService.createItem(
          "My Property",
          payload
        );
        setMyPropertyRecordId(resp.id ?? resp.Id);
      }

      toast.success("Saved successfully!");
      refetchMyProperty();
    } catch (error: any) {
      console.error(error);
      notify.error(error?.message ?? "Failed to save");
    }
  };

  const detailFields = [
    { label: "Property Name", key: "propertyname" },
    { label: "Year Built", key: "yearbuilt" },
    { label: "Per Sqft Price", key: "persqftprice" },
    { label: "Bed", key: "bed" },
    { label: "Bath", key: "bath" },
    { label: "Sq Footage", key: "sqfootage" },
    { label: "Address", key: "address" },
    { label: "Rough ARV", key: "rougharv" },
    { label: "Final ARV", key: "finalarv" },
    { label: "Notes", key: "notes" },
    { label: "Max Bid", key: "maxbid" },
    { label: "Rehab Estimate", key: "rehabestimate" },
  ];

  const handlefileuploadChange = (value: any) => {
    const safeValue = parseUploadValue(value);
    setfileuploads(prev => ({
      ...prev,
      fileupload: safeValue,
    }));
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);

      // Validate files
      if (fileArray.length === 0) {
        notify.error("No files selected");
        event.target.value = "";
        return;
      }

      // Check if all items are valid File objects
      const validFiles = fileArray.filter(file => file instanceof File);
      if (validFiles.length !== fileArray.length) {
        notify.error("Invalid file selection");
        event.target.value = "";
        return;
      }

      try {
        console.log("Uploading files:", {
          count: validFiles.length,
          folderName: "uploads",
          fileNames: validFiles.map(f => f.name),
          fileSizes: validFiles.map(f => f.size),
          fileTypes: validFiles.map(f => f.type),
        });

        // Upload files to server immediately to get file IDs
        // Use "uploads" folder name (same as Customfileupload component)
        const uploadResponse = await UploadService.uploadFiles({
          folderName: "uploads",
          files: validFiles,
        });

        console.log("Upload response:", uploadResponse);

        // Extract file IDs from response
        // Response format might be: [{id: "...", file_path: "..."}, ...] or {files: [{id: "...", ...}]}
        const uploadedFileIds: string[] = [];

        // Handle different response formats
        if (Array.isArray(uploadResponse)) {
          uploadResponse.forEach((fileResponse: any) => {
            // Extract ID from response (could be in different formats)
            const fileId =
              fileResponse?.id ??
              fileResponse?.file_id ??
              fileResponse?.fileId ??
              (typeof fileResponse === "string" ? fileResponse : null);
            if (fileId) {
              uploadedFileIds.push(String(fileId));
            }
          });
        } else if (
          uploadResponse?.files &&
          Array.isArray(uploadResponse.files)
        ) {
          // Response format: {files: [{id: "...", ...}]}
          uploadResponse.files.forEach((fileResponse: any) => {
            const fileId =
              fileResponse?.id ?? fileResponse?.file_id ?? fileResponse?.fileId;
            if (fileId) {
              uploadedFileIds.push(String(fileId));
            }
          });
        } else if (uploadResponse?.id) {
          // Single file response: {id: "...", ...}
          uploadedFileIds.push(String(uploadResponse.id));
        } else if (typeof uploadResponse === "string") {
          // Response is just a string ID
          uploadedFileIds.push(uploadResponse);
        }

        console.log("Extracted file IDs:", uploadedFileIds);

        if (uploadedFileIds.length === 0) {
          console.warn("No file IDs extracted from response:", uploadResponse);
          notify.error("Upload succeeded but no file IDs received");
          return;
        }

        // Append new file IDs to existing ones
        const currentFiles = Array.isArray(fileuploads.fileupload)
          ? fileuploads.fileupload
          : [];
        const updatedFiles = [...currentFiles, ...uploadedFileIds];
        handlefileuploadChange(updatedFiles);

        console.log("=== FILE UPLOAD COMPLETE ===");
        console.log("Current files before:", currentFiles);
        console.log("New file IDs:", uploadedFileIds);
        console.log("Updated files after:", updatedFiles);
        console.log("File uploads state will be:", {
          fileupload: updatedFiles,
        });

        toast.success(
          `${uploadedFileIds.length} file(s) uploaded. Don't forget to click Save to persist to schema!`
        );
      } catch (error: any) {
        console.error("Error uploading files:", error);
        console.error("Error response:", error?.response);
        console.error("Error response data:", error?.response?.data);
        console.error(
          "Error response data (stringified):",
          JSON.stringify(error?.response?.data, null, 2)
        );
        console.error("Error message:", error?.message);

        // Extract error message - handle nested error objects
        let errorMessage = "Failed to upload files. Please try again.";
        if (error?.response?.data) {
          const errorData = error.response.data;

          // Handle nested error object
          if (errorData.error) {
            if (typeof errorData.error === "string") {
              errorMessage = errorData.error;
            } else if (errorData.error.message) {
              errorMessage = errorData.error.message;
            } else if (errorData.error.detail) {
              errorMessage = errorData.error.detail;
            } else if (Array.isArray(errorData.error)) {
              errorMessage = errorData.error.join(", ");
            } else {
              errorMessage = JSON.stringify(errorData.error);
            }
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData)) {
            errorMessage = errorData.join(", ");
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        console.error("Final error message:", errorMessage);
        notify.error(errorMessage);
      } finally {
        // Reset input so same file can be selected again
        event.target.value = "";
      }
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRefs.current.fileupload?.click();
  };

  const getFileDisplayName = (file: any): string => {
    if (typeof file === "string") {
      // If it's a file ID, get name from fileInfoMap
      if (fileInfoMap[file]) {
        return (
          fileInfoMap[file]?.file_original_name ??
          fileInfoMap[file]?.name ??
          fileInfoMap[file]?.file_name ??
          "File"
        );
      }
      // If it's a URL, extract filename
      try {
        const url = new URL(file);
        return url.pathname.split("/").pop() ?? file;
      } catch {
        return file.length > 20 ? `${file.substring(0, 20)}...` : file;
      }
    }
    if (typeof file === "object" && file !== null) {
      return (
        file.name ??
        file.file_original_name ??
        file.url ??
        file.id ??
        file.file_path ??
        "File"
      );
    }
    return "File";
  };

  // Fetch file info for file IDs to display names and URLs
  useEffect(() => {
    const fetchFileInfos = async () => {
      const fileIds = fileuploads.fileupload.filter(
        (file: any) => typeof file === "string" && !file.startsWith("http")
      );

      const newFileInfoMap: Record<string, any> = { ...fileInfoMap };

      for (const fileId of fileIds) {
        if (!newFileInfoMap[fileId]) {
          try {
            const fileInfo = await UploadService.getFileById({ id: fileId });
            newFileInfoMap[fileId] = fileInfo;
          } catch (error) {
            console.error(`Error fetching file info for ${fileId}:`, error);
          }
        }
      }

      if (
        Object.keys(newFileInfoMap).length !== Object.keys(fileInfoMap).length
      ) {
        setFileInfoMap(newFileInfoMap);
      }
    };

    if (fileuploads.fileupload.length > 0) {
      fetchFileInfos();
    }
  }, [fileuploads.fileupload, fileInfoMap]);

  const getFileUrl = (file: any): string | null => {
    if (typeof file === "string") {
      // If it's a file ID, get URL from fileInfoMap
      if (fileInfoMap[file]) {
        return fileInfoMap[file]?.file_path ?? fileInfoMap[file]?.url ?? null;
      }
      // If it's already a URL, return it
      return file.startsWith("http") ? file : null;
    }
    if (typeof file === "object" && file !== null) {
      return file.url ?? file.file_path ?? file.file_path_optimized ?? null;
    }
    return null;
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = fileuploads.fileupload.filter((_, i) => i !== index);
    handlefileuploadChange(updatedFiles);
  };

  // Modify schema to restrict fields
  const restrictedSchema = useMemo(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.json ?? schemaJson;

    const modified = modifySchemaToRestrictFields(schema);
    return modified;
  }, [schemaJson]);

  // Set initial values when record is loaded
  useEffect(() => {
    if (recordInfo) {
      const initialData = removeMetaFields(recordInfo);
      form.setInitialValues(initialData);
      // Also set current values to ensure form fields are populated
      Object.keys(initialData).forEach(key => {
        form.setFieldState(key, state => {
          state.value = initialData[key];
        });
      });
    }
  }, [recordInfo, form]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) {
        notify.error("Property ID missing. Please reopen the page.");
        return;
      }

      // Save rehab estimate if we have a value (optional - don't block save if missing)
      if (rehabEstimateData.rehabestimate) {
        try {
          await saveRehabEstimate(rehabEstimateData.rehabestimate);
          toast.success("Rehab Estimate saved successfully!");
        } catch (rehabError) {
          console.error("Error saving rehab estimate:", rehabError);
          // Continue with saving My Property even if rehab estimate fails
        }
      }

      // ALWAYS save My Property data (files, remarks, notes, etc.)
      console.log("=== STARTING SAVE MY PROPERTY ===");
      console.log("File uploads state:", fileuploads);
      console.log("Remarks state:", remarks);
      console.log("Notes state:", additionalNotes);
      console.log("My Property Record ID:", myPropertyRecordId);

      try {
        await saveMyProperty();
        console.log("=== SAVE MY PROPERTY SUCCESS ===");
      } catch (saveError: any) {
        console.error("=== SAVE MY PROPERTY FAILED ===");
        console.error("Save error:", saveError);
        console.error("Error message:", saveError?.message);
        console.error("Error response:", saveError?.response);
        throw saveError; // Re-throw to be caught by outer catch
      }

      // Invalidate and refetch queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ["rehab-estimate", rehabEstimateSchemaName, id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["my-property", myPropertySchemaName, id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["my-property"],
      });

      // Refetch My Property data immediately to ensure we have the latest data
      await refetchMyProperty();

      toast.success("My Property details saved successfully!");

      // Small delay before navigation to ensure data is saved
      setTimeout(() => {
        navigate(navigationRoutes.MY_PROPERTY);
      }, 500);
    } catch (error: any) {
      console.error("=== SAVE ERROR ===");
      console.error("Save error:", error);
      console.error("Error message:", error?.message);
      console.error("Error response:", error?.response);

      let errorMessage = "Failed to save property details.";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage =
            typeof error.response.data.error === "string"
              ? error.response.data.error
              : JSON.stringify(error.response.data.error);
        }
      }

      notify.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate(navigationRoutes.MY_PROPERTY);
  };

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Edit Property
          </h1>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Hidden SchemaFormRender to maintain form state */}
          <div style={{ display: "none" }}>
            <SchemaFormRender form={form} schemaJson={restrictedSchema} />
          </div>

          <Card className="shadow-lg">
            <CardHeader className="border-b bg-muted/30">
              <div className="grid grid-cols-2 gap-4">
                <CardTitle className="text-sm font-semibold text-muted-foreground">
                  Field
                </CardTitle>
                <CardTitle className="text-sm font-semibold text-muted-foreground">
                  Value
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Additional Property Details */}
                {detailFields.map(field => {
                  const fieldValue = getFieldValue(field.key);
                  // Format value for display - handle arrays, objects, and null/undefined
                  let displayValue = "";
                  if (
                    fieldValue !== undefined &&
                    fieldValue !== null &&
                    fieldValue !== ""
                  ) {
                    if (Array.isArray(fieldValue)) {
                      displayValue = fieldValue
                        .map((item: any) => {
                          if (typeof item === "object" && item !== null) {
                            return (
                              item.url ??
                              item.name ??
                              item.id ??
                              JSON.stringify(item)
                            );
                          }
                          return String(item);
                        })
                        .join(", ");
                    } else if (typeof fieldValue === "object") {
                      displayValue =
                        fieldValue.url ??
                        fieldValue.name ??
                        fieldValue.id ??
                        JSON.stringify(fieldValue);
                    } else {
                      displayValue = String(fieldValue);
                    }
                  }

                  return (
                    <div
                      key={field.key}
                      className="grid grid-cols-2 items-center gap-4"
                    >
                      <Label>{field.label}</Label>
                      <Input
                        value={displayValue}
                        disabled
                        className="bg-muted"
                        placeholder="--"
                      />
                    </div>
                  );
                })}

                {/* Rehab Type Row */}
                <div className="grid grid-cols-2 items-center gap-4">
                  <Label>Rehab type</Label>
                  <Select value={rehabType} disabled>
                    <SelectTrigger id="rehabType">
                      <SelectValue placeholder="Select rehab type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EXCELLENT">EXCELLENT</SelectItem>
                      <SelectItem value="GOOD">GOOD</SelectItem>
                      <SelectItem value="OK">OK</SelectItem>
                      <SelectItem value="ROUGH">ROUGH</SelectItem>
                      <SelectItem value="VACANT ROUGH">VACANT ROUGH</SelectItem>
                      <SelectItem value="BAD">BAD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Rehab Value Row */}
                <div className="grid grid-cols-2 items-center gap-4">
                  <Label>Rehab value</Label>
                  <Input
                    id="rehabValue"
                    value={rehabValue}
                    disabled
                    placeholder="Select rehab type to see value"
                    className="bg-muted"
                  />
                </div>

                <div className="grid grid-cols-2 items-start gap-4">
                  <Label className="pt-2">File Upload</Label>
                  <div className="space-y-2">
                    {/* Display uploaded files */}
                    {fileuploads.fileupload.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {fileuploads.fileupload.map(
                          (file: any, index: number) => {
                            const fileUrl = getFileUrl(file);
                            const fileName = getFileDisplayName(file);
                            // Use file ID as key if available, otherwise use index
                            const fileKey =
                              typeof file === "string"
                                ? file
                                : (file?.id ?? index);
                            return (
                              <div
                                key={fileKey}
                                className="flex items-center justify-between rounded-md bg-muted p-2 text-sm"
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                  {fileUrl ? (
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="truncate text-primary hover:underline"
                                    >
                                      {fileName}
                                    </a>
                                  ) : (
                                    <span className="truncate">{fileName}</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(index)}
                                  className="ml-2 text-xs text-destructive hover:text-destructive/80"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                    {/* Upload button */}
                    <div className="relative">
                      <input
                        type="file"
                        ref={el => {
                          fileInputRefs.current.fileupload = el;
                        }}
                        onChange={handleFileInputChange}
                        multiple
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="justify-start"
                        onClick={handleUploadButtonClick}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload File
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 items-start gap-4">
                  <Label className="pt-2">Remarks</Label>
                  <Textarea
                    placeholder="Add remarks..."
                    value={remarks}
                    onChange={event => setRemarks(event.target.value)}
                    className="min-h-[100px]"
                  />
                </div>

                <div className="grid grid-cols-2 items-start gap-4">
                  <Label className="pt-2">Notes</Label>
                  <Textarea
                    placeholder="Add notes..."
                    value={additionalNotes}
                    onChange={event => setAdditionalNotes(event.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
            >
              Save
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default PP_EditRehabEstimator;
